import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalVariablesContext } from '../../context/GlobalVariablesContext';
import { useToolsContext } from '../../context/ToolsContext';
import MapContext from '../Map/MapContext';
import { getTool } from '../../utils/toolsHelper';
import './PrintMap.css';

/**
 * Componente de impresión del mapa en formato A4
 * Permite configurar orientación (vertical/horizontal), encabezados, pies de página, logos y otros elementos
 */
const PrintMap = () => {
    const { t } = useTranslation();
    const { map } = useContext(MapContext);
    const { config } = useGlobalVariablesContext();
    const { setToolState } = useToolsContext();
    const [isPrinting, setIsPrinting] = useState(false);
    const [showOrientationDialog, setShowOrientationDialog] = useState(false);
    const [selectedOrientation, setSelectedOrientation] = useState('portrait');
    const [titleValue, setTitleValue] = useState(() => config?.visorName || '');
    const [footerTextValue, setFooterTextValue] = useState('');

    // Obtener configuración de impresión desde config.json

    const printTool = getTool(config?.TOOLS, 'printMap');
    const printConfig = printTool?.params || {};
    const headerConfig = printConfig.header || {};
    const mapConfig = printConfig.map || {};
    const footerConfig = printConfig.footer || {};
    const pageConfig = printConfig.page || {};

    const resolveAssetUrl = (url) => {
        if (!url || typeof url !== 'string') return null;
        try {
            return new URL(url, window.location.href).toString();
        } catch {
            return url;
        }
    };

    const waitForImages = (rootEl, timeoutMs = 2500) => {
        if (!rootEl) return Promise.resolve();
        const imgs = Array.from(rootEl.querySelectorAll('img'));
        if (imgs.length === 0) return Promise.resolve();

        return Promise.all(
            imgs.map(
                (img) =>
                    new Promise((resolve) => {
                        if (img.complete) return resolve();

                        let done = false;
                        const finish = () => {
                            if (done) return;
                            done = true;
                            img.removeEventListener('load', finish);
                            img.removeEventListener('error', finish);
                            resolve();
                        };

                        const timer = window.setTimeout(finish, timeoutMs);
                        const finishWithTimerClear = () => {
                            window.clearTimeout(timer);
                            finish();
                        };

                        img.addEventListener('load', finishWithTimerClear, { once: true });
                        img.addEventListener('error', finishWithTimerClear, { once: true });
                    })
            )
        ).then(() => undefined);
    };

    // Función para capturar el mapa como imagen (export a canvas compuesto)
    // - Evita imprimir el canvas en vivo (suele salir negro en vista previa)
    // - Salta automáticamente canvases "tainted" (cross-origin), como Catastro si no hay CORS
    const captureMapAsImage = () => {
        if (!map) return Promise.resolve(null);

        return new Promise((resolve) => {
            const size = map.getSize();
            if (!size || size.length < 2) {
                resolve(null);
                return;
            }

            const viewport = map.getViewport();
            if (!viewport) {
                resolve(null);
                return;
            }

            const onRenderComplete = () => {
                try {
                    const mapCanvas = document.createElement('canvas');
                    mapCanvas.width = size[0];
                    mapCanvas.height = size[1];

                    const ctx = mapCanvas.getContext('2d');
                    if (!ctx) {
                        resolve(null);
                        return;
                    }

                    // Canvases de OpenLayers por capa (ordenados por DOM)
                    const layerCanvases = viewport.querySelectorAll('.ol-layer canvas');
                    if (!layerCanvases || layerCanvases.length === 0) {
                        resolve(null);
                        return;
                    }

                    layerCanvases.forEach((canvas) => {
                        if (!canvas || canvas.width === 0 || canvas.height === 0) return;

                        // Si el canvas está tainted (por tiles sin CORS), esto lanza DOMException
                        try {
                            // eslint-disable-next-line no-unused-expressions
                            canvas.toDataURL;
                            canvas.toDataURL('image/png');
                        } catch (e) {
                            // Saltamos esta capa (p.ej. Catastro sin CORS)
                            return;
                        }

                        const parent = canvas.parentNode;
                        const opacity =
                            (parent && parent.style && parent.style.opacity !== '' ? parent.style.opacity : '') ||
                            (parent ? window.getComputedStyle(parent).opacity : '1');

                        ctx.globalAlpha = opacity === '' ? 1 : Number(opacity);

                        const transform = canvas.style.transform;
                        if (transform && transform.startsWith('matrix(')) {
                            const matrix = transform
                                .slice(7, -1)
                                .split(',')
                                .map((v) => Number(v.trim()));

                            if (matrix.length === 6 && matrix.every((n) => Number.isFinite(n))) {
                                ctx.setTransform(matrix[0], matrix[1], matrix[2], matrix[3], matrix[4], matrix[5]);
                            } else {
                                ctx.setTransform(1, 0, 0, 1, 0, 0);
                            }
                        } else {
                            ctx.setTransform(1, 0, 0, 1, 0, 0);
                        }

                        // Fondo si la capa lo define
                        if (parent && parent.style && parent.style.backgroundColor) {
                            ctx.fillStyle = parent.style.backgroundColor;
                            ctx.fillRect(0, 0, canvas.width, canvas.height);
                        }

                        ctx.drawImage(canvas, 0, 0);
                    });

                    // Reset transform/alpha
                    ctx.setTransform(1, 0, 0, 1, 0, 0);
                    ctx.globalAlpha = 1;

                    try {
                        resolve(mapCanvas.toDataURL('image/png'));
                    } catch (e) {
                        resolve(null);
                    }
                } catch (e) {
                    resolve(null);
                }
            };

            // Esperamos a un render completo y forzamos render sync
            map.once('rendercomplete', onRenderComplete);
            try {
                map.renderSync();
            } catch (e) {
                // Si falla renderSync, igualmente intentamos resolver en el siguiente tick
                setTimeout(() => resolve(null), 0);
            }
        });
    };

    // Función para preparar la página para impresión
    const preparePrintPage = async (orientation = 'portrait', title = '', footerText = '') => {
        const mapImage = await captureMapAsImage();
        const currentDate = new Date().toLocaleDateString('es-ES');
        const currentTime = new Date().toLocaleTimeString('es-ES');

        // Por defecto usamos el logo blanco del proyecto y lo pintamos a negro con CSS
        const logoUrl = resolveAssetUrl(headerConfig?.logo?.url || '/images/logo_w.svg');
        // Si no se especifica logo.show, asumimos true (para que "se vea algo" por defecto)
        const logoEnabled = headerConfig?.logo?.show !== false;
        const logoPosition = headerConfig?.logo?.position || 'left'; // left | right | both
        const showLogoLeft = Boolean(logoEnabled && logoUrl && (logoPosition === 'left' || logoPosition === 'both'));
        const showLogoRight = Boolean(logoEnabled && logoUrl && (logoPosition === 'right' || logoPosition === 'both'));
        const forceBlackLogo =
            headerConfig?.logo?.color === 'black' ||
            headerConfig?.logo?.forceBlack === true ||
            /logo_w\.svg(\?|#|$)/i.test(logoUrl || '');

        // Crear un elemento temporal para la impresión
        const printElement = document.createElement('div');
        printElement.id = 'print-content';
        
        const mapContent = mapImage
            ? `<img src="${mapImage}" alt="Mapa" class="print-map-image" />`
            : `
                <div class="print-map-placeholder">
                    <div class="placeholder-text">No se puede exportar el mapa</div>
                    <div class="placeholder-subtext">Alguna capa no permite impresión por CORS (p.ej. Catastro). Pruebe a desactivar esa capa.</div>
                </div>
            `;

        printElement.innerHTML = `
            <div class="print-container">
                ${headerConfig.show ? `
                <div class="print-header">
                    <div class="print-logo print-logo-left">
                        ${showLogoLeft ? `<img src="${logoUrl}" alt="Logo" class="logo-image ${forceBlackLogo ? 'logo-image--black' : ''}" />` : ''}
                    </div>
                    <div class="print-header-content">
                    ${title ? `<h1 class="print-title">${title}</h1>` : ''}
                    </div>
                    <div class="print-logo print-logo-right">
                        ${showLogoRight ? `<img src="${logoUrl}" alt="Logo" class="logo-image ${forceBlackLogo ? 'logo-image--black' : ''}" />` : ''}
                    </div>
                </div>
                ` : ''}

                <div class="print-map-container">
                ${mapContent}
                
                ${mapConfig.showNorthArrow !== false ? `
                    <div class="print-north-arrow">
                    <div class="north-arrow-symbol">↑</div>
                    <div class="north-arrow-text">N</div>
                    </div>
                ` : ''}
                </div>

                ${footerConfig.show ? `
                <div class="print-footer">
                    <div class="print-footer-content">
                    ${footerText ? `<div class="print-footer-text">${footerText}</div>` : ''}
                    ${footerConfig.showDate ? `
                        <div class="print-date">Fecha: ${currentDate} - Hora: ${currentTime}</div>
                    ` : ''}
                    </div>
                </div>
                ` : ''}
            </div>
        `;

        // Añadir estilos CSS para impresión
        const style = document.createElement('style');
        style.setAttribute('data-print-styles', 'true');

        style.textContent = `
            @page {
                size: ${pageConfig.size || 'A4'} ${orientation};
                margin: ${pageConfig.margins || '1cm'};
            }
            
            @media print {
                * {
                    margin: 0 !important;
                    padding: 0 !important;
                }

                /* Forzar "papel" blanco aunque la app sea dark */
                html, body {
                    background: #fff !important;
                    color: #000 !important;
                }

                /* Ocultar todo excepto print-content */
                body > *:not(#print-content) {
                    display: none !important;
                }

                /* Asegurar que NO se cuele el mapa real (canvas en vivo) */
                .ol-viewport,
                .ol-overlaycontainer,
                .ol-overlaycontainer-stopevent,
                canvas {
                    display: none !important;
                    visibility: hidden !important;
                }
                
                #print-content {
                    position: absolute !important;
                    left: 0 !important;
                    top: 0 !important;
                    width: 100% !important;
                    z-index: 9999 !important;
                    background: #fff !important;
                    min-height: 100vh !important;
                }
                
                .sidebar, .tools-container, .layer-selector, .right-sidebar,
                .tools-container-right, .offcanvas, .modal, .dropdown,
                .btn, .navbar, .header, .left-sidebar, .right-sidebar,
                .tools-bar, .control-panel, .map-controls, .layer-controls,
                .toolbar, .menu, .selector-de-capas, .capas, .herramientas {
                    display: none !important;
                    visibility: hidden !important;
                }

                #print-content {
                    width: 100%;
                    margin: 0;
                    padding: 0;
                    display: block !important;
                    visibility: visible !important;
                    background: #fff !important;
                }
                
                .print-container {
                    width: 100%;
                    background: #fff !important;
                    min-height: 100vh !important;
                }
            }
        `;

        // Añadir al documento
        document.head.appendChild(style);
        document.body.appendChild(printElement);

        // Asegurar que el logo/imagenes han cargado antes de imprimir
        await waitForImages(printElement);

        return printElement;
    };

    // Función para ocultar elementos de la interfaz
    const hideInterfaceElements = () => {
        // En lugar de manipular el DOM, usamos CSS media print
        // Esto evita problemas con elementos re-renderizados
        return null;
    };

    // Función para restaurar elementos de la interfaz
    const restoreInterfaceElements = () => {
        // No hay nada que restaurar con CSS media print
    };

    // Función para limpiar después de imprimir
    const cleanupPrintElements = () => {
        const printElement = document.getElementById('print-content');
        const printStyles = document.querySelector('style[data-print-styles]');

        if (printElement) {
            printElement.remove();
        }
        if (printStyles) {
            printStyles.remove();
        }
    };

    // Función para mostrar el diálogo de selección de orientación
    const handlePrint = () => {
        if (!map) return;
        // Activar la herramienta para que el gestor central desactive el resto
        setToolState('printMap', { active: true, sidebarOpen: false, type: 'clickable' });
        setShowOrientationDialog(true);
    };

    const closeOrientationDialog = () => {
        setShowOrientationDialog(false);
        // No dejamos esta herramienta "activa" si el usuario cancela/cierra el diálogo
        setToolState('printMap', { active: false, sidebarOpen: false });
    };

    // Función para ejecutar la impresión con la orientación seleccionada
    const executePrint = async () => {
        setShowOrientationDialog(false);
        setIsPrinting(true);

        try {
            // Ocultar elementos de la interfaz usando CSS en lugar de manipular el DOM
            hideInterfaceElements();

            // Preparar la página para impresión con la orientación seleccionada
            await preparePrintPage(selectedOrientation, titleValue, footerTextValue);

            // Esperar un momento para que se renderice
            setTimeout(() => {
                // Imprimir
                window.print();

                // Limpiar después de un delay
                setTimeout(() => {
                    // Restaurar elementos de la interfaz
                    restoreInterfaceElements();
                    cleanupPrintElements();
                    setIsPrinting(false);
                    setToolState('printMap', { active: false, sidebarOpen: false });
                }, 1000);
            }, 500);
        } catch (error) {
            console.error('Error al preparar la impresión:', error);
            setIsPrinting(false);
            setToolState('printMap', { active: false, sidebarOpen: false });
        }
    };

    return (
        <>
            <span
                className={`list-item ${isPrinting ? 'disabled' : ''}`}
                onClick={isPrinting ? undefined : handlePrint}
                style={{
                    cursor: isPrinting ? 'not-allowed' : 'pointer',
                    opacity: isPrinting ? 0.6 : 1
                }}
                title={t('Imprimir Mapa')}
            >
                <i className="bi bi-printer-fill"></i>
            </span>

            {showOrientationDialog && (
                <div
                    className="orientation-dialog-overlay"
                    onClick={closeOrientationDialog}
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 10000
                    }}>
                    <div
                        className="orientation-dialog"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '24px',
                            minWidth: '420px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}>
                        <h4 style={{ marginBottom: '20px', color: '#333', fontSize: '18px', fontWeight: '600' }}>
                            {t('Seleccionar Orientación')}
                        </h4>

                        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                            <div
                                onClick={() => setSelectedOrientation('portrait')}
                                style={{
                                    flex: 1, padding: '16px',
                                    border: selectedOrientation === 'portrait' ? '2px solid var(--overPrimaryColor)' : '2px solid #ddd',
                                    borderRadius: '6px', cursor: 'pointer', textAlign: 'center',
                                    backgroundColor: selectedOrientation === 'portrait' ? '#e7f3ff' : 'white',
                                    transition: 'all 0.2s'
                                }}>
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                                    <i className="bi bi-phone"></i>
                                </div>
                                <div style={{ fontWeight: selectedOrientation === 'portrait' ? '600' : '400', color: '#333' }}>
                                    {t('Vertical')}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                    {t('Portrait')}
                                </div>
                            </div>
                            <div
                                onClick={() => setSelectedOrientation('landscape')}
                                style={{
                                    flex: 1, padding: '16px',
                                    border: selectedOrientation === 'landscape' ? '2px solid var(--overPrimaryColor)' : '2px solid #ddd',
                                    borderRadius: '6px', cursor: 'pointer', textAlign: 'center',
                                    backgroundColor: selectedOrientation === 'landscape' ? '#e7f3ff' : 'white',
                                    transition: 'all 0.2s'
                                }}>
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                                    <i className="bi bi-phone-landscape"></i>
                                </div>
                                <div style={{ fontWeight: selectedOrientation === 'landscape' ? '600' : '400', color: '#333' }}>
                                    {t('Horizontal')}
                                </div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                    {t('Landscape')}
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#555', fontWeight: '500' }}>
                                {t('Título')}
                            </label>
                            <input
                                type="text"
                                value={titleValue}
                                onChange={(e) => setTitleValue(e.target.value)}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#555', fontWeight: '500' }}>
                                {t('Pie de página')}
                            </label>
                            <input
                                type="text"
                                value={footerTextValue}
                                onChange={(e) => setFooterTextValue(e.target.value)}
                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
                            />
                        </div>

                        <div className='d-flex justify-content-between gap-2 mb-2'>
                            <button
                                onClick={closeOrientationDialog}
                                className="btn btn-secondary w-100">
                                {t('Cancelar')}
                            </button>
                            <button
                                onClick={executePrint}
                                className="btn btn-primary w-100">
                                <i className="bi bi-printer-fill" style={{ marginRight: '6px' }}></i>
                                {t('Imprimir')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PrintMap;
